import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

const DEFAULTS = {
  site_name: "Voyage Luxe",
  site_tagline: "Luxury Journeys Across India & Beyond",
  phone: "+91 95556 47288",
  email: "concierge@voyageluxe.com",
  whatsapp: "919555647288",
  address: "11 Aurangzeb Road, New Delhi 110011, India",
  social_links: JSON.stringify({ instagram: "", facebook: "", twitter: "", youtube: "" }, null, 2),
  seo_defaults: JSON.stringify({ title_suffix: " — Voyage Luxe", og_image: "" }, null, 2),
  notification_email: "admin@voyageluxe.com",
  quote_response_hours: "24",
};

export default function AdminSettings() {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get("/settings").then((r) => {
      const data = r.data || {};
      setForm((f) => ({
        ...f,
        ...Object.fromEntries(
          Object.entries(data).map(([k, v]) => [k, typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)])
        ),
      }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Parse JSON fields
      const payload = { ...form };
      ["social_links", "seo_defaults"].forEach((k) => {
        try { payload[k] = JSON.parse(payload[k]); } catch { /* keep as string */ }
      });
      await adminApi.post("/settings/bulk", payload);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-accent transition-colors px-4 py-3 outline-none text-sm";
  const labelCls = "eyebrow block mb-2";

  if (loading) return <div className="text-foreground/50 text-center py-20">Loading…</div>;

  return (
    <form onSubmit={save} className="space-y-8 max-w-4xl" data-testid="admin-settings">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-3">Configuration</div>
          <h1 className="font-serif text-4xl md:text-5xl">Site settings.</h1>
          <p className="mt-3 text-foreground/60 text-sm">Global configuration for your website. Changes apply immediately.</p>
        </div>
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em] disabled:opacity-50">
          <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save all"}
        </button>
      </div>

      {/* Brand */}
      <section className="border border-border p-6 md:p-8 space-y-6">
        <h2 className="font-serif text-2xl">Brand & Identity</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><label className={labelCls}>Site name</label><input value={form.site_name} onChange={set("site_name")} className={inputCls} /></div>
          <div><label className={labelCls}>Tagline</label><input value={form.site_tagline} onChange={set("site_tagline")} className={inputCls} /></div>
        </div>
      </section>

      {/* Contact */}
      <section className="border border-border p-6 md:p-8 space-y-6">
        <h2 className="font-serif text-2xl">Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={set("phone")} className={inputCls} /></div>
          <div><label className={labelCls}>Email</label><input value={form.email} onChange={set("email")} className={inputCls} /></div>
          <div><label className={labelCls}>WhatsApp number (with country code, no +)</label><input value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} /></div>
          <div><label className={labelCls}>Notification email (inquiry alerts)</label><input value={form.notification_email} onChange={set("notification_email")} className={inputCls} /></div>
        </div>
        <div><label className={labelCls}>Address</label><input value={form.address} onChange={set("address")} className={inputCls} /></div>
      </section>

      {/* Social */}
      <section className="border border-border p-6 md:p-8 space-y-6">
        <h2 className="font-serif text-2xl">Social Links (JSON)</h2>
        <textarea rows={5} value={form.social_links} onChange={set("social_links")} className={`${inputCls} font-mono text-xs resize-y`} />
      </section>

      {/* SEO */}
      <section className="border border-border p-6 md:p-8 space-y-6">
        <h2 className="font-serif text-2xl">SEO Defaults (JSON)</h2>
        <textarea rows={4} value={form.seo_defaults} onChange={set("seo_defaults")} className={`${inputCls} font-mono text-xs resize-y`} />
        <div><label className={labelCls}>Quote response hours</label><input value={form.quote_response_hours} onChange={set("quote_response_hours")} className={inputCls} /></div>
      </section>
    </form>
  );
}
